import styled, { css, keyframes } from 'styled-components';
import { blueLink, grey, grey10, grey25, navy, white } from '@zocdoc/branding/colors';

import { NumColumns } from '../../grid';
import { Arrow } from '../../icons';
import { FontFamily } from '../../typography';

import {
    DESKTOP_DAY_COLUMN_SIZE,
    DESKTOP_WIDTH_MATCH_SLOT_AREA,
} from '../constants/ViewConstants';

const fadeIn = keyframes`
    from { opacity: 0.01; }
    to { opacity: 1; }
`;

const loading = keyframes`
    0% { opacity: 1 }
    100% { opacity: 0.3 }
`;

const SLOT_HEIGHT_PX = 32;
const MOBILE_SLOT_WIDTH = 88;
const MOBILE_SLOT_MARGIN_RIGHT = 8;

const baseSlotStyles = css`
    align-items: center;
    border-radius: 0;
    border: 1px solid;
    box-sizing: border-box;
    color: inherit;
    display: flex;
    font: 13px/1 ${FontFamily.SharpSansSemibold};
    height: ${SLOT_HEIGHT_PX}px;
    justify-content: center;
    min-width: 20px;
    text-align: center;
    text-decoration: inherit;
    width: 100%;
`;

// if global.theme is set, this is a screenshot test
const INITIAL_SLOT_OPACITY = global.theme ? 1 : 0;

const slotStyles = css`
    animation: 0.1s ${fadeIn} ease-in 1 forwards;
    background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColor};
    border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColor};
    border-radius: 0;
    box-sizing: border-box;
    color: ${p => p.theme.components.AppointmentPicker.slot.textColor};
    cursor: pointer;
    opacity: ${INITIAL_SLOT_OPACITY};
`;

const hoverActiveSlotStyles = css`
    &:hover {
        background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorHover};
        border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorHover};
        color: ${p => p.theme.components.AppointmentPicker.slot.textColorHover};
    }

    &:active {
        background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorActive};
        border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorActive};
        color: ${p => p.theme.components.AppointmentPicker.slot.textColorActive};
    }
`;

const desktopSlotStyles = css`
    ${slotStyles}
    ${hoverActiveSlotStyles}
`;

const mobileSlotStyles = css`
    ${slotStyles}

    @media (pointer: fine) {
        ${hoverActiveSlotStyles}
    }
`;


export const DesktopSlot = styled.a`
    ${baseSlotStyles}
    ${desktopSlotStyles}
`;

export const DesktopMoreButton = DesktopSlot.withComponent('button');

export const DesktopEmptySlot = styled.div`
    ${baseSlotStyles}

    background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorEmpty};
    border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorEmpty};
    color: ${p => p.theme.components.AppointmentPicker.slot.textColorEmpty};
    user-select: none;
`;

export const DesktopLoadingSlot = styled(DesktopEmptySlot)`
    animation: ${loading} 750ms ease-in-out infinite alternate;
    cursor: wait;
`;

export const DesktopNonBookableMessage = styled.div`
    ${baseSlotStyles}

    background-color: transparent;
    border-color: transparent;
    color: ${grey};
`;

export const GovernmentInsuranceLink = styled.a`
    color: ${blueLink};
    border-bottom: 1px dotted ${blueLink};

    &:hover {
        border-bottom-style: solid;
    }
`;

const linkStyles = css`
    background-color: ${grey10};
    border-color: ${grey10};
    color: ${navy};
    cursor: pointer;
    padding-left: 16px;
    padding-right: 16px;
    text-align: center;
    width: ${DESKTOP_WIDTH_MATCH_SLOT_AREA};
`;

const hoverLinkStyles = css`
    &:hover {
        background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorHover};
        border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorHover};
        color: ${p => p.theme.components.AppointmentPicker.slot.textColorHover};
    }
`;

const activeLinkStyles = css`
    &:active {
        background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorActive};
        border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorActive};
        color: ${p => p.theme.components.AppointmentPicker.slot.textColorActive};
    }
`;

const desktopLinkStyles = css`
    ${linkStyles}
    ${hoverLinkStyles}
    ${activeLinkStyles}
`;

const mobileLinkStyles = css`
    ${linkStyles}
    ${activeLinkStyles}

    @media (pointer: fine) {
        ${hoverLinkStyles}
    }
`;

export const DesktopNextAvailabilityLink = styled.button`
    ${baseSlotStyles}
    ${desktopLinkStyles}
`;

export const DesktopPhoneNumberLink = styled.a`
    ${baseSlotStyles}
    ${desktopLinkStyles}
`;

export const DesktopWrapper = styled.div`
    position: relative;
`;

export const DesktopGridRow = styled.div`
    display: flex;
    user-select: none;
    width: 100%;

    &:not(:first-child) {
        margin-top: 8px;
    }
`;

/**
 * using a transparent left/right border instead of margin because
 * margin will screw up the alignment with the controls above
 */
export const DesktopGridCell = styled.div`
    box-sizing: border-box;
    border: 4px solid transparent;
    border-top: 0;
    border-bottom: 0;
    text-align: center;
    width: ${p => (DESKTOP_DAY_COLUMN_SIZE[p.sizeId] / NumColumns) * 100}%;
`;

export const DesktopNavigationPlaceholderColumn = styled.div`
    min-height: 1px;
    width: ${(2 / NumColumns) * 100}%;
`;

const existingPatientLinkStyles = css`
    background-color: transparent;
    border-color: ${grey25};
    color: ${navy};
    cursor: pointer;
    margin: 8px auto 0;
    padding-left: 16px;
    padding-right: 16px;
    width: ${DESKTOP_WIDTH_MATCH_SLOT_AREA};
`;

const hoverActiveExistingPatientLinkStyles = css`
    background-color: ${navy};
    border-color: ${navy};
    color: #FFF;
`;

const hoverExistingPatientLinkStyles = css`
    &:hover {
        ${hoverActiveExistingPatientLinkStyles}
    }
`;

const activeExistingPatientLinkStyles = css`
    &:active {
        ${hoverActiveExistingPatientLinkStyles}
    }
`;


const desktopExistingPatientLinkStyles = css`
    ${existingPatientLinkStyles}
    ${hoverExistingPatientLinkStyles}
    ${activeExistingPatientLinkStyles}
`;

const mobileExistingPatientLinkStyles = css`
    ${existingPatientLinkStyles}
    ${activeExistingPatientLinkStyles}

    @media (pointer: fine) {
        ${hoverExistingPatientLinkStyles}
    }
`;

export const DesktopExistingPatientLink = styled.a`
    ${baseSlotStyles}
    ${desktopExistingPatientLinkStyles}
`;

export const DesktopExistingPatientLinkText = styled.span`
    flex-grow: 1;
    text-align: center;
`;

export const DesktopExistingPatientLinkArrow = styled(Arrow)`
    flex-shrink: 0;
    margin-left: auto;
`;

export const DesktopNoTimesView = styled.div`
    align-items: center;
    background-color: ${p => p.isWellGuideView ? '#F6F6F6' : '#FFF'};
    display: flex;
    flex-direction: column;
    font-family: ${FontFamily.SharpSansMedium};
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    text-align: center;
`;

const mobileBaseSlotStyles = css`
    ${baseSlotStyles}

    padding: 5px 0;
`;

export const MobileSlot = styled.a`
    ${mobileBaseSlotStyles}
    ${mobileSlotStyles}
`;

export const MobileMoreButton = MobileSlot.withComponent('button');
export const MobileEmptySlot = styled.div`
    ${mobileBaseSlotStyles}

    background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorEmpty};
    border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorEmpty};
    color: ${p => p.theme.components.AppointmentPicker.slot.textColorEmpty};
    cursor: normal;
    user-select: none;
`;


export const MobileLoadingSlot = styled(MobileSlot)`
    animation: ${loading} 750ms ease-in-out infinite alternate;
    background-image: repeating-linear-gradient(to right, ${grey10}, ${grey10} ${MOBILE_SLOT_WIDTH}px, white ${MOBILE_SLOT_WIDTH}px, white ${MOBILE_SLOT_WIDTH + MOBILE_SLOT_MARGIN_RIGHT}px);
    border-color: transparent !important;
    cursor: wait;
    opacity: 1;
`;

export const MobileNonBookableMessage = styled.div`
    ${mobileBaseSlotStyles}

    color: ${grey};
    background-color: transparent;
    border-color: transparent;
    padding-left: 16px;
    padding-right: 16px;
    width: 100%;
`;

export const MobileNoTimesView = styled(DesktopNoTimesView)`
    position: relative;
    width: 100%;
`;

export const MobileNextAvailabilityLink = styled.button`
    ${mobileBaseSlotStyles}
    ${mobileLinkStyles}
    width: 100%;
    display: block;
`;

export const MobilePhoneNumberLink = styled.a`
    ${mobileBaseSlotStyles}
    ${mobileLinkStyles}
    width: 100%;
`;

export const MobileExistingPatientLink = styled.a`
    ${mobileBaseSlotStyles}
    ${mobileExistingPatientLinkStyles}
    width: 100%;
`;

export const MobileWrapper = styled.div`
    background-color: ${white};
    display: flex;
    position: relative;
    min-height: ${SLOT_HEIGHT_PX}px;
`;

export const MobileScroller = styled.div`
    -webkit-overflow-scrolling: touch;
    min-height: ${SLOT_HEIGHT_PX}px;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    white-space: nowrap;
`;

export const MobileSlotWrapper = styled.div`
    display: inline-block;
    margin-right: ${MOBILE_SLOT_MARGIN_RIGHT}px;
    width: ${MOBILE_SLOT_WIDTH}px;
`;

export const MobileStyledArrow = styled(Arrow)`
    flex-shrink: 0;
    margin-left: auto;
`;

export const MobileExistingPatientLinkText = styled.span`
    flex-grow: 1;
    text-align: center;
`;
